import { useMemo } from 'react';

import {
  AGENT_ACTIVITY_METADATA_KEY,
  AGENT_STATUS_METADATA_KEY,
  DocumentTagID,
  type AgentStatus,
  type ChatMessage,
  type ChatSessionMode,
  type ConversationalFormOption,
} from '@karehero/models';

import { useSelector } from 'store';
import { useListChatSessionsQuery } from 'store/api/chatSession';
import { getCurrentCareCircle } from 'store/slices/careCircleSlice';
import { toChatMessage } from 'utils/chatMessages';
import {
  answersMessage,
  openQuestion,
  shownQuestions,
} from 'utils/serverChatQuestions';
import { useChatSender } from './useChatSender';
import { useChatSessionWriter } from './useChatSessionWriter';
import { useMessageOutbox } from './useMessageOutbox';
import { useServerChatHaptics } from './useServerChatHaptics';

export interface UseServerChatOptions {
  /** The mode the session runs in; without one it waits. */
  mode?: ChatSessionMode;
  sessionId: string;
}

/**
 * useServerChat shows a chat session of a mode the server runs, sends what is
 * typed or said into it, and answers the questions it asks. Nothing is read yet.
 */
export const useServerChat = ({ mode, sessionId }: UseServerChatOptions) => {
  const careCircleId = useSelector(getCurrentCareCircle)?.id;

  // The session is read from its mode's list, which is the cache sent messages land in.
  const queryArgs = useMemo(
    () => ({ careCircleId: careCircleId ?? '', mode: mode ?? '' }),
    [careCircleId, mode],
  );
  const { data: sessions } = useListChatSessionsQuery(queryArgs, {
    skip: !careCircleId || !mode,
    refetchOnMountOrArgChange: true,
  });

  const sessionData = sessions?.find(({ session }) => session.id === sessionId);
  const saved = useMemo<ChatMessage[]>(
    () => (sessionData?.messages ?? []).map((m) => toChatMessage(m)),
    [sessionData?.messages],
  );
  const messages = useMemo(() => shownQuestions(saved), [saved]);
  const question = useMemo(() => openQuestion(saved), [saved]);
  // The agent shows nothing but the profile as a card, once there's nothing left to find out.
  const profileId = useMemo(
    () =>
      [...saved]
        .reverse()
        .find((m) => m.from === 'system' && m.content.kind === 'card')?.id,
    [saved],
  );
  // What is already in the session when it loads is not felt.
  useServerChatHaptics({ messages, isReady: !!sessionData });

  const { persistMessage, updateMessage } = useChatSessionWriter({
    careCircleId,
    sessionId: sessionData && sessionId,
    queryArgs,
  });
  const { send: sendMessage, resend: resendMessage } =
    useMessageOutbox(updateMessage);
  const sender = useChatSender({
    careCircleId,
    source: mode ?? '',
    documentTag: DocumentTagID.Conversation,
    context: '',
    // The server reads nothing from these conversations yet.
    canRead: false,
    persistMessage,
    updateMessage,
    sendMessage,
  });

  return {
    careCircleId,
    messages,
    // The server says when it is working on a turn, in the session's metadata.
    isAgentThinking:
      sessionData?.session.metadata?.[AGENT_STATUS_METADATA_KEY] ===
      ('thinking' satisfies AgentStatus),
    /** What it has done so far in the turn it's working on: the latest, if anything. */
    agentActivity:
      sessionData?.session.metadata?.[AGENT_ACTIVITY_METADATA_KEY]?.at(-1),
    // And when it is waiting on something sent being read.
    isAgentReading:
      sessionData?.session.metadata?.[AGENT_STATUS_METADATA_KEY] ===
      ('reading' satisfies AgentStatus),
    isSessionReady: !!sessionData,
    // Loaded, and the session isn't among its mode's.
    isSessionMissing: !!sessions && !sessionData,
    /** The question the server is asking, when it is asking one. */
    question,
    /** The profile the conversation ended on, once it has been shown. */
    profileId,
    /** Answers the question being asked with the options picked. */
    answer: (options: ConversationalFormOption[]) => {
      if (question) persistMessage(answersMessage(question, options));
    },
    sendText: sender.sendText,
    sendVoiceNote: sender.sendVoiceNote,
    sendVoiceWords: sender.sendVoiceWords,
    attachVoiceRecording: sender.attachVoiceRecording,
    /** Sends files, read once they are processed. */
    sendFiles: sender.sendFiles,
    resendMessage,
  };
};
